import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import find from 'lodash/find';
import { useContext } from 'react';

import { ConstantsLagoon } from '../../../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../../../common/Utils';
import { PortalContext } from '../../../../../../context/PortalContext';
import { useLagoon } from '../../../../../../hooks/useLagoon';
import { LayoutGroupStyled } from '../../../CompareSmartChoice/CompareSmartChoice.styled';
import { isAttributeChecked } from '../../../CompareSmartChoice/utils';
import { NULL_ATTRIBUTE_STATE } from '../../CompareDrawer/utility/compareDrawerConstants';
import { getEducation } from '../../CompareDrawer/utility/getEducationText';
import {
  IconWithText,
  PremiumCareContainer,
  PremiumCareIconsContainer,
  PremiumCareRow,
  PremiumCareSubText,
  PremiumCareText,
  PrintViewDivider,
  PrintViewProviderBoldText,
  PrintViewProviderDetailsXSText,
  Section,
} from './PrintExpandedCardStyle.styled';

export const PrintExpandedQualityCard = ({
  selectedAttributes,
  selectedProviders,
  isSmartChoice,
}) => {
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_SERVICE_DATA_AVAILABLE,
  });
  const no_service_data_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_NO_SERVICE_DATA_AVAILABLE,
  });

  const enableLLMMedicalSpecialists: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LLM_MEDICAL_SPECIALISTS
  );

  const credentialed_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_CREDENTIALED_PROVIDER,
  })?.message;
  const edu_not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_EDUCATION_INFO_NOT_AVAILABLE,
  })?.message;
  const not_a_cred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_NOT_A_CREDENTIALED_PROVIDER,
  })?.message;
  const comparePCPText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_PCP_TEXT,
  })?.message;
  const comparePCPSubText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_PCP_SUBTEXT,
  })?.message;
  const compareNoPCPText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_NO_PCP_TEXT,
  })?.message;
  const compareNoPCPSubText = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_QUALITY_NO_PCP_SUBTEXT,
  })?.message;

  const education = (item) => getEducation(item.educationInfo);
  const isUniversityEmpty = (item) =>
    education(item).schoolName === NULL_ATTRIBUTE_STATE.EDUCATION_SCHOOLS;
  const schoolName = (item) => {
    const { schoolName } = getEducation(item?.educationInfo || []);
    return schoolName;
  };
  const degreeCode = (item) => {
    const { degreeCode } = getEducation(item?.educationInfo || []);
    return degreeCode;
  };

  const isEducationInfoEmpty = (provider) =>
    !provider?.educationInfo ||
    !Array.isArray(provider.educationInfo) ||
    provider.educationInfo.length === 0 ||
    degreeCode(provider) === NULL_ATTRIBUTE_STATE.EDUCATION_DEGREES ||
    schoolName(provider) === NULL_ATTRIBUTE_STATE.EDUCATION_SCHOOLS;

  const { portalData } = useContext(PortalContext);

  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  return (
    <Section>
      {selectedAttributes?.find(
        (record) => record.name === 'premiumCareProvider'
      )?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                {item?.recommendationDetails?.quality?.premiumDesignation ===
                  'Y' ||
                ((isGuestUser || !isSmartChoice) &&
                  item?.isPremiumCarePhysician === 'YES') ? (
                  <PremiumCareContainer>
                    <PremiumCareRow>
                      <PremiumCareIconsContainer>
                        <IconSymbol color="#002677" icon="favorite" size={14} />
                        <IconSymbol color="#002677" icon="favorite" size={14} />
                      </PremiumCareIconsContainer>
                      <PremiumCareText>{comparePCPText}</PremiumCareText>
                    </PremiumCareRow>
                  </PremiumCareContainer>
                ) : (
                  <PremiumCareContainer>
                    <PremiumCareRow>
                      <PremiumCareText>{compareNoPCPText}</PremiumCareText>
                    </PremiumCareRow>
                  </PremiumCareContainer>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                {item?.recommendationDetails?.quality?.premiumDesignation ===
                  'Y' ||
                (!isSmartChoice && item?.isPremiumCarePhysician === 'YES') ? (
                  <PremiumCareContainer>
                    <PremiumCareSubText>{comparePCPSubText}</PremiumCareSubText>
                  </PremiumCareContainer>
                ) : (
                  <PremiumCareContainer>
                    <PremiumCareSubText>
                      {compareNoPCPSubText}
                    </PremiumCareSubText>
                  </PremiumCareContainer>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {selectedAttributes?.find((record) => record?.name === 'credentials')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <Box
                  css={{
                    padding: '2px 4px',
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                  }}
                >
                  {item?.recommendationDetails?.quality?.credentialed ===
                  'Y' ? (
                    <IconWithText>
                      <IconSymbol color="#007000" icon="done" size={20} />
                      <PrintViewProviderBoldText>
                        {credentialed_provider}
                      </PrintViewProviderBoldText>
                    </IconWithText>
                  ) : (
                    <PrintViewProviderDetailsXSText>
                      {not_a_cred_provider}
                    </PrintViewProviderDetailsXSText>
                  )}
                </Box>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {!isSmartChoice &&
            isAttributeChecked(selectedAttributes, ['education']) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {selectedProviders?.map((_, index) => (
                  <PrintViewDivider key={index} orientation="horizontal" />
                ))}
              </LayoutGroupStyled>
            )}
          {isSmartChoice &&
            isAttributeChecked(selectedAttributes, ['serviceData']) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {selectedProviders?.map((_, index) => (
                  <PrintViewDivider key={index} orientation="horizontal" />
                ))}
              </LayoutGroupStyled>
            )}
        </>
      )}
      {selectedAttributes?.find((record) => record.name === 'education')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) =>
              isEducationInfoEmpty(item) ? (
                <Flex direction="column" key={index}>
                  <PrintViewProviderDetailsXSText>
                    {edu_not_available}
                  </PrintViewProviderDetailsXSText>
                </Flex>
              ) : (
                <Flex css={{ flexDirection: 'column' }} key={index}>
                  <PrintViewProviderBoldText
                    cssProps={{
                      isUniversityEmpty: isUniversityEmpty(item),
                    }}
                  >
                    {schoolName(item)}
                  </PrintViewProviderBoldText>
                  <PrintViewProviderDetailsXSText>
                    ({degreeCode(item)})
                  </PrintViewProviderDetailsXSText>
                </Flex>
              )
            )}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {selectedAttributes?.find((record) => record.name === 'serviceData')
        ?.isChecked && (
        <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
          {selectedProviders?.map(
            (item, index) =>
              enableLLMMedicalSpecialists &&
              item?.recommendationDetails?.quality?.llmStatus !== 'NA' && (
                <PrintViewProviderDetailsXSText key={index}>
                  {item?.recommendationDetails?.quality?.llmStatus === '1'
                    ? service_data_available?.message
                    : no_service_data_available?.message}
                </PrintViewProviderDetailsXSText>
              )
          )}
        </LayoutGroupStyled>
      )}
    </Section>
  );
};
